import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Button, Typography } from '@mui/material';
import DataDisplay, {
  useResponsiveDataDisplayVariant,
} from '@sharedCR/coreDesignSystem/DataDisplay';
import TooltipWrapper from 'app/shared/components/common/TooltipWrapper';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';

import { FeatureFlag } from 'constants/featureFlags';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import usePendingChangeAuthors from 'hooks/usePendingChangeAuthors';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';

import { getLoanFeesSummary } from 'store/loanApplications/actions';
import { selectHasLoanCicReasons, selectLoanFeesSummary } from 'store/loanApplications/selectors';

import { LoanFeesTestIds } from '../LoanFees.testIds';
import { useOpenLoanCicReasonsModal } from '../LoanFeesBody/LoanFeeSection/components/ManageCICModal/hooks/useOpenLoanCicReasonsModal';

import CashToCloseData from './CashToCloseData';
import CureData from './CureData';
import FeesSummarySkeleton from './FeesSummarySkeleton';
import { getSummaryInfoConfig } from './LoanFeesHead.config';
import { FEE_TOOLTIP_POPPER_MODIFIERS } from './LoanFeesHead.constants';
import {
  getSummaryWrapperStyles,
  skeletonWrapperStyles,
  summaryCardStyles,
  summaryHeaderStyles,
  titleTypographyStyles,
  wrapperStyles,
} from './LoanFeesHead.styles';

const LoanFeesHead = ({ isLoading, setIsLoading }) => {
  const { applicationId } = useParams();
  const loanId = useLoanId();

  const dispatch = useDispatch();

  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();

  const dataDisplayVariant = useResponsiveDataDisplayVariant();

  const isManageCicHistoryEnabled = useFeatureToggle(FeatureFlag.EnableManageCicHistory);

  const hasLoanCicReasons = useSelector(selectHasLoanCicReasons);

  const openLoanCicReasonsModal = useOpenLoanCicReasonsModal();

  const isLoanCicReasonsShown = isManageCicHistoryEnabled && hasLoanCicReasons;

  const {
    closingDate,
    fundingDate,
    firstPaymentDate,
    cashToCloseDetailedSummary,
    cureSummary,
    apr,
    savedApr,
    modifiedFields,
    modifiedFieldDetails,
  } = useSelector(selectLoanFeesSummary);
  const loanFieldPendingChangeAuthors = usePendingChangeAuthors(loanId, applicationId);

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    dispatch(getLoanFeesSummary({ applicationId }))
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [applicationId, dispatch, setIsLoading]);

  const cashToCloseData = useMemo(
    () => <CashToCloseData data={cashToCloseDetailedSummary} />,
    [cashToCloseDetailedSummary],
  );

  const cureData = useMemo(() => <CureData data={cureSummary} />, [cureSummary]);

  const summaryInfo = useMemo(
    () =>
      getSummaryInfoConfig({
        closingDate,
        fundingDate,
        firstPaymentDate,
        cashToCloseData,
        finalCashToClose: cashToCloseDetailedSummary?.finalCashToClose,
        cureData,
        totalCure: cureSummary?.totalCure,
        apr,
        savedApr,
        modifiedFields,
        modifiedFieldDetails,
        loanFieldPendingChangeAuthors,
      }),
    [
      closingDate,
      fundingDate,
      firstPaymentDate,
      cashToCloseData,
      cashToCloseDetailedSummary?.finalCashToClose,
      cureData,
      cureSummary?.totalCure,
      apr,
      savedApr,
      modifiedFields,
      modifiedFieldDetails,
      loanFieldPendingChangeAuthors,
    ],
  );

  if (isLoading) {
    return (
      <Box sx={skeletonWrapperStyles}>
        <FeesSummarySkeleton />
      </Box>
    );
  }

  return (
    <Box sx={wrapperStyles}>
      <Box sx={summaryHeaderStyles}>
        <Typography sx={titleTypographyStyles} variant="regular14">
          Summary
        </Typography>

        {isLoanCicReasonsShown && (
          <Button
            color="primary"
            data-testid={LoanFeesTestIds.HEAD_CIC_REASONS_BUTTON}
            variant="text"
            onClick={openLoanCicReasonsModal}
          >
            Loan-Level CIC Reasons
          </Button>
        )}
      </Box>

      <Box
        sx={getSummaryWrapperStyles(summaryInfo.length)}
        onMouseLeave={onResetTooltipState}
        onMouseOver={onMouseOver}
      >
        <TruncatedProvider />

        {summaryInfo.map(
          ({
            name,
            value,
            icon,
            iconColor,
            withInfoIcon,
            disableInteractive,
            hoverContent,
            customTooltipStyles,
            changeKind,
            pendingChangeAuthors,
          }) => {
            const card = (
              <Box sx={summaryCardStyles}>
                <DataDisplay
                  changeKind={changeKind}
                  icon={icon}
                  iconColor={iconColor}
                  isAdditionalIcon={withInfoIcon}
                  pendingChangeAuthors={pendingChangeAuthors}
                  title={name}
                  value={value}
                  variant={dataDisplayVariant}
                />
              </Box>
            );

            return hoverContent ? (
              <TooltipWrapper
                disableInteractive={disableInteractive}
                key={name}
                popperModifiers={FEE_TOOLTIP_POPPER_MODIFIERS}
                title={hoverContent}
                tooltipSx={customTooltipStyles}
              >
                {card}
              </TooltipWrapper>
            ) : (
              <React.Fragment key={name}>{card}</React.Fragment>
            );
          },
        )}
      </Box>
    </Box>
  );
};

LoanFeesHead.propTypes = {
  ...WithLoadingPropTypes,
};

export default withLoading(LoanFeesHead);
